import { useState } from "react";
import { createFileRoute, Outlet, useMatches } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/dashboard-layout";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { queuesQueryOptions, deleteQueueFn } from "@/server/queues";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard/queues")({
  component: QueueRouteComponent,
  loader: async () => {
    return {
      crumb: "Queues",
    };
  },
});

function QueueRouteComponent() {
  const queuesQuery = useQuery(queuesQueryOptions);
  const queues = queuesQuery.data ?? [];

  const queryClient = useQueryClient();
  const matches = useMatches();
  const isRootQueues = matches[matches.length - 1].fullPath === "/queues";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<{ id: number; name: string } | null>(null);
  const [loading, setLoading] = useState(false);

  function handleDeleteClick(queueId: number, queueName: string) {
    setSelectedQueue({ id: queueId, name: queueName });
    setDialogOpen(true);
  }

  const deleteQueueMutation = useMutation({
    mutationFn: deleteQueueFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queues"] });
      queryClient.invalidateQueries({ queryKey: ["queue-group-queues"] });
      queuesQuery.refetch();
      setDialogOpen(false);
      setSelectedQueue(null);
      setLoading(false);
    },
  });

  async function handleConfirmDelete() {
    if (!selectedQueue) return;
    setLoading(true);
    const deleteQueuePromise = deleteQueueMutation.mutateAsync({
      data: { queueId: Number(selectedQueue.id) },
    });
    toast.promise(deleteQueuePromise, {
      loading: "Deleting queue...",
      success: () => {
        return `Queue "${selectedQueue.name}" has been deleted successfully`;
      },
      error: (error) => error.message || "Failed to delete queue",
    });
  }

  return (
    <>
      <DashboardLayout
        submenu={{
          items: queues,
          onClick: (item: any, functionType: string, parent?: { name: string; id: string }) => {
            if (functionType === "delete") {
              handleDeleteClick(item.parent.id, item.parent.name);
            }
          },
          title: "Queues",
          active: true,
          createNew: [{ label: "+ Create new queue", href: "/queues/new-queue" }],
        }}
      >
        {isRootQueues ? (
          <Card>
            <CardContent>
              <p className="text-md pb-20 pt-15 text-center">
                Please select a Queue from the submenu to view its details or manage it.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Outlet />
        )}
      </DashboardLayout>
      <ConfirmDialog
        open={dialogOpen}
        title="Delete queue"
        description={selectedQueue ? `Are you sure you want to delete the queue "${selectedQueue.name}"?` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedQueue(null);
        }}
        loading={loading}
      />
    </>
  );
}
